import React, { useContext, useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { Box, Button } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import LegacyTicketsQueueSelect from "../LegacyTicketsQueueSelect";
import ChatLayoutToggle from "../ChatLayout/ChatLayoutToggle";
import RecentTicketSearches from "../RecentTicketSearches";
import useRecentTicketSearches from "../../hooks/useRecentTicketSearches";

const useStyles = makeStyles((theme) => ({
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	tabsHeader: {
		flex: "none",
		backgroundColor: theme.palette.background.default,
	},

	settingsIcon: {
		alignSelf: "center",
		marginLeft: "auto",
		padding: 8,
	},

	tab: {
		minWidth: 120,
		width: 120,
	},

	ticketOptionsBox: {
		position: "relative",
		zIndex: 6,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		background: theme.palette.background.default,
		padding: theme.spacing(1),
	},

	serachInputWrapper: {
		position: "relative",
		flex: 1,
		background: "#fff",
		display: "flex",
		borderRadius: 40,
		padding: 4,
		marginRight: theme.spacing(1),
	},

	searchIcon: {
		color: "grey",
		marginLeft: 6,
		marginRight: 6,
		alignSelf: "center",
	},

	searchInput: {
		flex: 1,
		border: "none",
		borderRadius: 30,
	},

	badge: {
		right: "-10px",
	},
	show: {
		display: "block",
	},
	hide: {
		display: "none !important",
	},
}));

function LegacyTicketsManager({ tailwickLayout = false }) {
	const classes = useStyles();

	const [searchParam, setSearchParam] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [tab, setTab] = useState("open");
	const [tabOpen, setTabOpen] = useState("open");
	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const [showAllTickets, setShowAllTickets] = useState(false);
	const searchInputRef = useRef();
	const searchTimeoutRef = useRef();
	const { user } = useContext(AuthContext);
	const {
		items: recentSearches,
		add: addRecentSearch,
		remove: removeRecentSearch,
		clear: clearRecentSearches,
	} = useRecentTicketSearches(user?.id);
	const [searchFocused, setSearchFocused] = useState(false);
	const darkMode = localStorage.getItem("darkMode") === "true";

	const [openCount, setOpenCount] = useState(0);
	const [pendingCount, setPendingCount] = useState(0);
	const [selectedTags, setSelectedTags] = useState([]);

	const userQueueIds = user.queues?.map((q) => q.id);
	const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
	useEffect(() => {
		if (user.profile.toUpperCase() === "ADMIN") {
			setShowAllTickets(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (tab === "search") {
			searchInputRef.current.focus();
		}
	}, [tab]);

	const applySearchTerm = (raw) => {
		const searchedTerm = String(raw || "").trim();
		setSearchParam(searchedTerm.toLowerCase());
		if (!searchedTerm) {
			setTab("open");
			return;
		}
		setTab("search");
		addRecentSearch(searchedTerm);
	};

	const handleSearch = (e) => {
		const searchedTerm = e.target.value;
		setSearchInput(searchedTerm);

		clearTimeout(searchTimeoutRef.current);

		if (searchedTerm.trim() === "") {
			setSearchParam("");
			setTab("open");
			return;
		}

		searchTimeoutRef.current = setTimeout(() => {
			applySearchTerm(searchedTerm);
		}, 500);
	};

	const handleSelectRecent = (term) => {
		clearTimeout(searchTimeoutRef.current);
		setSearchInput(term);
		setSearchParam(term);
		setTab("search");
		addRecentSearch(term);
	};

	const handleSelectedTags = (selecteds) => {
		const tags = selecteds.map((t) => t.id);
		setSelectedTags(tags);
	};

	const handleChangeTab = (e, newValue) => {
		setTab(newValue);
	};

	const handleChangeTabOpen = (e, newValue) => {
		setTabOpen(newValue);
	};

	const applyPanelStyle = (status) => {
		if (tabOpen !== status) {
			return { width: 0, height: 0 };
		}
	};

	return (
		<Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
			<NewTicketModal
				modalOpen={newTicketModalOpen}
				onClose={(e) => setNewTicketModalOpen(false)}
			/>
			<Box
				px={1}
				py={0.75}
				display="flex"
				alignItems="center"
				style={{ flexShrink: 0, minHeight: 44, maxWidth: "100%", overflow: "hidden" }}
			>
				<ChatLayoutToggle compact />
			</Box>
			<Paper elevation={0} square className={classes.tabsHeader}>
				<Tabs
					value={tab}
					onChange={handleChangeTab}
					variant="fullWidth"
					indicatorColor="primary"
					textColor="primary"
					aria-label="icon label tabs example"
				>
					<Tab
						value="open"
						icon={<MoveToInboxIcon />}
						label={i18n.t("tickets.tabs.open.title")}
						classes={{ root: classes.tab }}
					/>
					<Tab
						value="closed"
						icon={<CheckBoxIcon />}
						label={i18n.t("tickets.tabs.closed.title")}
						classes={{ root: classes.tab }}
					/>
					<Tab
						value="search"
						icon={<SearchIcon />}
						label={i18n.t("tickets.tabs.search.title")}
						classes={{ root: classes.tab }}
					/>
				</Tabs>
			</Paper>
			<Paper square elevation={0} className={classes.ticketOptionsBox}>
				{tab === "search" ? (
					<div className={classes.serachInputWrapper}>
						<SearchIcon className={classes.searchIcon} />
						<InputBase
							className={classes.searchInput}
							inputRef={searchInputRef}
							placeholder={i18n.t("tickets.search.placeholder")}
							type="search"
							value={searchInput}
							autoComplete="off"
							onFocus={() => setSearchFocused(true)}
							onBlur={() => setSearchFocused(false)}
							onChange={handleSearch}
						/>
						<RecentTicketSearches
							open={searchFocused && !searchInput.trim()}
							items={recentSearches}
							dark={darkMode}
							onSelect={handleSelectRecent}
							onRemove={removeRecentSearch}
							onClear={clearRecentSearches}
						/>
					</div>
				) : (
					<>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setNewTicketModalOpen(true)}
						>
							{i18n.t("ticketsManager.buttons.newTicket")}
						</Button>
						<Can
							role={user.profile}
							perform="tickets-manager:showall"
							yes={() => (
								<FormControlLabel
									label={i18n.t("tickets.buttons.showAll")}
									labelPlacement="start"
									control={
										<Switch
											size="small"
											checked={showAllTickets}
											onChange={() =>
												setShowAllTickets((prevState) => !prevState)
											}
											name="showAllTickets"
											color="primary"
										/>
									}
								/>
							)}
						/>
					</>
				)}
				<LegacyTicketsQueueSelect
					style={{ marginLeft: 6 }}
					selectedQueueIds={selectedQueueIds}
					userQueues={user?.queues}
					onChange={(values) => setSelectedQueueIds(values)}
				/>
			</Paper>
			<TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
				<Tabs
					value={tabOpen}
					onChange={handleChangeTabOpen}
					indicatorColor="primary"
					textColor="primary"
					variant="fullWidth"
				>
					<Tab
						label={
							<Badge
								overlap="rectangular"
								className={classes.badge}
								badgeContent={openCount}
								color="primary"
							>
								{i18n.t("ticketsList.assignedHeader")}
							</Badge>
						}
						value="open"
					/>
					<Tab
						label={
							<Badge
								overlap="rectangular"
								className={classes.badge}
								badgeContent={pendingCount}
								color="secondary"
							>
								{i18n.t("ticketsList.pendingHeader")}
							</Badge>
						}
						value="pending"
					/>
				</Tabs>
				<Paper className={classes.ticketsWrapper}>
					<TicketsList
						status="open"
						showAll={showAllTickets}
						selectedQueueIds={selectedQueueIds}
						style={applyPanelStyle("open")}
						reportUnreadBadgeCount={setOpenCount}
					/>
					<TicketsList
						status="pending"
						selectedQueueIds={selectedQueueIds}
						style={applyPanelStyle("pending")}
						reportUnreadBadgeCount={setPendingCount}
					/>
				</Paper>
			</TabPanel>
			<TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
				<TicketsList
					status="closed"
					showAll
					selectedQueueIds={selectedQueueIds}
				/>
			</TabPanel>
			<TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
				<TagsFilter onFiltered={handleSelectedTags} />
				<TicketsList
					searchParam={searchParam}
					tags={selectedTags}
					showAll
					selectedQueueIds={selectedQueueIds}
				/>
			</TabPanel>
		</Paper>
	);
}

export default LegacyTicketsManager;
